// Attachments (attachments.md section 8): an assistant-ui AttachmentAdapter that uploads each file
// the person adds straight to their AlphaLab Drive, one at a time. Quanty One's API asks AlphaLab for a
// one-time link (POST /api/attachments); the browser PUTs the bytes to it. Quanty One never sees the
// bytes, and assistant-ui never reads the file into the page: a sent attachment has no content.
import type { Attachment, AttachmentAdapter, CompleteAttachment, PendingAttachment } from "@assistant-ui/react";
import { post } from "./api.js";

/** A file on a message, as the API stores it: where it is in Drive, its name, size and type. */
export interface AttachmentRef {
  path: string;
  name: string;
  sizeBytes: number;
  contentType: string;
}

/** What /api/me offers where the environment asks for both Drive scopes. */
export interface AttachmentLimits {
  maxFiles: number;
  maxBytes: number;
}

export type UploadPhase = "waiting" | "requesting" | "uploading" | "done" | "failed";

/** One attachment in the composer, as its chip shows it (section 5, States). */
export interface Upload {
  id: string;
  name: string;
  sizeBytes: number;
  phase: UploadPhase;
  /** Bytes sent so far. */
  sent: number;
  /** Why it failed, for the person. */
  reason?: string;
  /** Its Drive path, once Quanty One named it; a retry writes the same one. */
  path?: string;
  /** Once AlphaLab stored it: what the message sends. */
  ref?: AttachmentRef;
}

/** A PUT's answer: status 0 when no answer came (a dropped connection, the tab's abort). */
export interface PutResult {
  status: number;
  /** Seconds, from Retry-After. */
  retryAfter?: number;
  message?: string;
}

export interface UploadTransport {
  /** POST /api/attachments. */
  requestLink(body: Record<string, unknown>): Promise<{ status: number; data: Record<string, unknown> }>;
  /** PUTs the file to the link, reporting bytes sent. */
  put(url: string, file: File, contentType: string, onProgress: (sent: number) => void, signal: AbortSignal): Promise<PutResult>;
  sleep(ms: number): Promise<void>;
}

/** A size as the person reads it: 1,024 bytes a KB, so the 1 GiB cap reads as 1.0 GB. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB"];
  let value = n / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value >= 100 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/** The type the file is sent and named with: the browser's, without parameters, or plain bytes. */
export function contentTypeOf(file: Pick<File, "type">): string {
  const type = file.type.split(";")[0].trim().toLowerCase();
  return /^[a-z0-9][a-z0-9!#$&^_.+-]{0,126}\/[a-z0-9][a-z0-9!#$&^_.+-]{0,126}$/.test(type) ? type : "application/octet-stream";
}

/** The file's own name; a pasted file may have none. */
function nameOf(file: Pick<File, "name">): string {
  return file.name.trim() ? file.name : "file";
}

/** A wait AlphaLab asked for, in milliseconds: its Retry-After, kept between 1 second and a minute. */
function waitOf(seconds: unknown): number {
  const s = typeof seconds === "number" && Number.isFinite(seconds) ? seconds : 10;
  return Math.min(Math.max(s, 1), 60) * 1000;
}

function percent(u: Pick<Upload, "sent" | "sizeBytes">): number {
  return u.sizeBytes ? Math.min(100, Math.floor((u.sent / u.sizeBytes) * 100)) : 0;
}

/** Why the link was not given, for the person (section 7.2's answers). */
function linkRefusal(status: number, data: Record<string, unknown>): string {
  const said = typeof data.message === "string" && data.message ? data.message : undefined;
  if (status === 401 || data.error === "reconnect_for_scope") return "Reconnect AlphaLab to attach files.";
  if (status === 409) return "Another account is signed in here. Reload the page.";
  if (data.error === "drive_refused") return said ?? "AlphaLab refused the file: your Drive is full, or your plan does not allow it.";
  if (status === 429) return "AlphaLab is busy. Retry in a minute.";
  if (status === 400) return "Quanty One could not take this file.";
  if (status === 404) return "Attachments are not available here.";
  if (status === 502) return said ?? "AlphaLab did not give an upload link. Retry.";
  return "AlphaLab could not be reached. Retry in a moment.";
}

/** Why the PUT did not store the file, for the person (section 4, Interruption). */
function putRefusal(put: PutResult, u: Upload): string {
  if (put.status === 403) return put.message ?? "AlphaLab refused the file: your Drive is full, or today's transfer allowance is used up.";
  if (put.status === 413) return "The file changed while it was sent. Retry reads it again.";
  if (put.status === 429 || put.status === 503) return "AlphaLab is busy. Retry in a minute.";
  if (put.status === 410) return "The upload link expired. Retry.";
  if (put.status === 400) return put.message ?? "AlphaLab refused the file.";
  return `Upload stopped at ${percent(u)}%. Retry sends the whole file again.`;
}

interface Entry {
  file: File;
  abort: AbortController;
}

/**
 * The composer's uploads: the AttachmentAdapter assistant-ui calls, and the store the chips read.
 * `add` checks size and count, waits its turn, asks for the link and uploads, yielding `running`
 * until it ends `requires-action` (uploaded) or `incomplete` (failed). Send waits for uploads still
 * running; `send` refuses a failed one, so the message stays in the composer (A4).
 */
export class DriveUploads implements AttachmentAdapter {
  readonly accept = "*";
  private readonly uploads = new Map<string, Upload>();
  private readonly entries = new Map<string, Entry>();
  /** Files whose Retry was pressed: `add` gives them their id and path again. */
  private readonly retrying = new WeakMap<File, { id: string; path?: string }>();
  private readonly listeners = new Set<() => void>();
  private refusals: string[] = [];
  /** One upload at a time (section 9): who holds the turn, and who waits. */
  private holding = false;
  private readonly queue: (() => void)[] = [];

  constructor(
    private readonly limits: AttachmentLimits,
    private readonly transport: UploadTransport,
  ) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  get(id: string): Upload | undefined {
    return this.uploads.get(id);
  }

  /** Refusals of the last files added, one line each: never added (section 5, `refused`). */
  get notices(): readonly string[] {
    return this.refusals;
  }

  dismissNotices(): void {
    this.refusals = [];
    this.notify();
  }

  /** A file still on its way: leaving the page loses it. */
  get busy(): boolean {
    return [...this.uploads.values()].some((u) => u.phase !== "done" && u.phase !== "failed");
  }

  /** A failed upload is in the composer: Send waits for its Retry or Remove (A4). */
  get blocked(): boolean {
    return [...this.uploads.values()].some((u) => u.phase === "failed");
  }

  /** The file to add again for Retry; `add` then uploads it under the same id and Drive path. */
  retryFile(id: string): File | undefined {
    const u = this.uploads.get(id);
    const e = this.entries.get(id);
    if (!u || !e || u.phase !== "failed") return undefined;
    this.retrying.set(e.file, { id, ...(u.path ? { path: u.path } : {}) });
    return e.file;
  }

  /** What a sent message carries for these attachments; they leave the composer. */
  sent(ids: readonly string[]): AttachmentRef[] {
    const refs = ids.flatMap((id) => {
      const ref = this.uploads.get(id)?.ref;
      return ref ? [ref] : [];
    });
    for (const id of ids) this.forget(id);
    this.dismissNotices();
    return refs;
  }

  async *add({ file }: { file: File }): AsyncGenerator<PendingAttachment, void> {
    const again = this.retrying.get(file);
    this.retrying.delete(file);
    if (!again) {
      const refused = this.refusal(file);
      if (refused) {
        // Files added together are checked one by one: each refusal is kept until dismissed or sent.
        this.refusals = [...this.refusals, refused].slice(-this.limits.maxFiles);
        this.notify();
        throw new Error(refused);
      }
    }
    const id = again?.id ?? crypto.randomUUID();
    const abort = new AbortController();
    this.entries.get(id)?.abort.abort();
    this.entries.set(id, { file, abort });
    this.set({ id, name: nameOf(file), sizeBytes: file.size, phase: "waiting", sent: 0, ...(again?.path ? { path: again.path } : {}) });
    yield pending(id, file, { type: "running", reason: "uploading", progress: 0 });

    const release = await this.turn(abort.signal);
    // A removal frees the slot at once, whatever this upload still waits on.
    abort.signal.addEventListener("abort", release, { once: true });
    let end: Upload | undefined;
    try {
      if (!abort.signal.aborted) end = await this.transfer(id, file, abort.signal);
    } finally {
      release();
    }
    // Removed meanwhile: nothing more to show.
    if (!end || abort.signal.aborted) return;
    yield pending(id, file, end.phase === "done" ? { type: "requires-action", reason: "composer-send" } : { type: "incomplete", reason: "error", message: end.reason });
  }

  async remove(attachment: Attachment): Promise<void> {
    // An upload in flight is aborted, so nothing is stored; a file already stored stays in Drive.
    this.forget(attachment.id);
  }

  async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    const u = this.uploads.get(attachment.id);
    if (u?.phase !== "done") throw new Error(u?.reason ?? "This file did not upload.");
    // No content: the file is in Drive, and the message names it by path.
    return { id: attachment.id, type: attachment.type, name: attachment.name, contentType: attachment.contentType, status: { type: "complete" }, content: [] };
  }

  private refusal(file: File): string | undefined {
    const name = nameOf(file);
    if (file.size === 0) return `${name} is empty.`;
    if (file.size > this.limits.maxBytes) return `${name} is over ${formatBytes(this.limits.maxBytes)}.`;
    if (this.uploads.size >= this.limits.maxFiles) return `${name} was not added: a message takes up to ${this.limits.maxFiles} files.`;
    return undefined;
  }

  /**
   * The link, then the bytes (section 5, steps 2 to 4), with one wait for a busy AlphaLab and one new
   * link. Undefined once the attachment was removed.
   */
  private async transfer(id: string, file: File, signal: AbortSignal): Promise<Upload | undefined> {
    const contentType = contentTypeOf(file);
    let newLink = false;
    for (;;) {
      this.update(id, { phase: "requesting", sent: 0 });
      const path = this.uploads.get(id)?.path;
      const body = { name: nameOf(file), sizeBytes: file.size, contentType, ...(path ? { path } : {}) };
      let res = await this.requestLink(body);
      if (res.status === 429 && !signal.aborted) {
        await this.wait(waitOf(res.data.retryAfter), signal);
        if (signal.aborted) return undefined;
        res = await this.requestLink(body);
      }
      if (signal.aborted) return undefined;
      if (res.status !== 200) return this.failed(id, linkRefusal(res.status, res.data));
      const ref: AttachmentRef = { path: String(res.data.path), name: String(res.data.name), sizeBytes: file.size, contentType };
      this.update(id, { path: ref.path });
      // A retry whose earlier PUT landed after all: nothing is sent again.
      if (res.data.stored === true) return this.update(id, { phase: "done", sent: file.size, ref });
      const url = (res.data.upload as { url?: unknown } | undefined)?.url;
      if (typeof url !== "string") return this.failed(id, linkRefusal(502, {}));

      this.update(id, { phase: "uploading" });
      const onProgress = (sent: number) => {
        const u = this.uploads.get(id);
        // The chip moves a whole percent at a time.
        if (u && percent({ sent, sizeBytes: u.sizeBytes }) !== percent(u)) this.update(id, { sent });
      };
      let put = await this.transport.put(url, file, contentType, onProgress, signal);
      // Throttled before any byte was read: the link was released, so the same link once more.
      if ((put.status === 429 || put.status === 503) && !signal.aborted) {
        await this.wait(waitOf(put.retryAfter), signal);
        if (signal.aborted) return undefined;
        this.update(id, { sent: 0 });
        put = await this.transport.put(url, file, contentType, onProgress, signal);
      }
      if (signal.aborted) return undefined;
      if (put.status === 200) return this.update(id, { phase: "done", sent: file.size, ref });
      // Expired or used before the PUT started: no bytes moved, so a new link once.
      if (put.status === 410 && !newLink) {
        newLink = true;
        continue;
      }
      // No automatic retry otherwise: each attempt spends the day's transfer allowance.
      return this.failed(id, putRefusal(put, this.uploads.get(id)!));
    }
  }

  /** AlphaLab's wait, cut short by a removal. */
  private wait(ms: number, signal: AbortSignal): Promise<void> {
    if (signal.aborted) return Promise.resolve();
    return new Promise((resolve) => {
      signal.addEventListener("abort", () => resolve(), { once: true });
      void this.transport.sleep(ms).then(() => resolve());
    });
  }

  private async requestLink(body: Record<string, unknown>): Promise<{ status: number; data: Record<string, unknown> }> {
    try {
      return await this.transport.requestLink(body);
    } catch {
      return { status: 0, data: {} };
    }
  }

  /** Waits for the one upload slot; a removal while waiting gives up its place. */
  private turn(signal: AbortSignal): Promise<() => void> {
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      const next = this.queue.shift();
      if (next) next();
      else this.holding = false;
    };
    if (!this.holding) {
      this.holding = true;
      return Promise.resolve(release);
    }
    return new Promise((resolve) => {
      const go = () => {
        signal.removeEventListener("abort", leave);
        resolve(release);
      };
      const leave = () => {
        const i = this.queue.indexOf(go);
        if (i >= 0) this.queue.splice(i, 1);
        resolve(() => undefined);
      };
      this.queue.push(go);
      signal.addEventListener("abort", leave, { once: true });
    });
  }

  private failed(id: string, reason: string): Upload {
    return this.update(id, { phase: "failed", reason });
  }

  private set(u: Upload): Upload {
    this.uploads.set(u.id, u);
    this.notify();
    return u;
  }

  /** A new state for the chip (a new object, as useSyncExternalStore needs); a reason goes with its failure. */
  private update(id: string, change: Partial<Upload>): Upload {
    const u = this.uploads.get(id) ?? { id, name: "", sizeBytes: 0, sent: 0, phase: "failed" as const };
    const { reason: _r, ...rest } = u;
    const next = { ...(change.phase === undefined || change.phase === "failed" ? u : rest), ...change };
    // A removed attachment is not brought back.
    return this.uploads.has(id) ? this.set(next) : next;
  }

  private forget(id: string): void {
    this.entries.get(id)?.abort.abort();
    this.entries.delete(id);
    if (this.uploads.delete(id)) this.notify();
  }

  private notify(): void {
    for (const l of this.listeners) l();
  }
}

function pending(id: string, file: File, status: PendingAttachment["status"]): PendingAttachment {
  const type = file.type.startsWith("image/") ? "image" : "document";
  return { id, type, name: nameOf(file), contentType: contentTypeOf(file), file, status };
}

/**
 * The real transport: the API with its headers, and XMLHttpRequest for the PUT, whose upload events
 * give progress. `onSession` hears a 401 or 409, after which the app reads /api/me again.
 */
export function browserTransport(userId: string, onSession: () => void): UploadTransport {
  return {
    requestLink: async (body) => {
      const res = await post("/api/attachments", body, { "x-quanty-user": userId });
      if (res.status === 401 || res.status === 409) onSession();
      return res;
    },
    put: xhrPut,
    sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  };
}

function xhrPut(url: string, file: File, contentType: string, onProgress: (sent: number) => void, signal: AbortSignal): Promise<PutResult> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve({ status: 0 });
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    // The link is the only credential: no cookies go to AlphaLab.
    xhr.withCredentials = false;
    xhr.setRequestHeader("content-type", contentType);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      let message: string | undefined;
      try {
        const body = JSON.parse(xhr.responseText) as { message?: unknown; error?: unknown };
        message = typeof body.message === "string" ? body.message : typeof body.error === "string" ? body.error : undefined;
      } catch {
        // Not JSON.
      }
      const retryAfter = Number(xhr.getResponseHeader("retry-after"));
      resolve({ status: xhr.status, ...(Number.isFinite(retryAfter) && retryAfter > 0 ? { retryAfter } : {}), ...(message ? { message } : {}) });
    };
    xhr.onerror = xhr.onabort = xhr.ontimeout = () => resolve({ status: 0 });
    signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}
